import React from 'react';
import {
  FormLabel,
  FormLabelProps,
  TextFieldProps as MuiTextFieldProps,
  Stack,
  styled,
  TextField,
} from '@mui/material';
import { AsYouTypeUS } from 'utils/phoneNumberUtils/phoneNumberUtils';

const StyledLabel = styled(FormLabel)<FormLabelProps>(({ theme }) => ({
  color: theme.palette.grey[800],
}));

interface PhoneInputProps extends Omit<MuiTextFieldProps, 'onChange'> {
  id: string;
  onChange?: (value: string) => void;
}

export const PhoneInput: React.FC<PhoneInputProps> = ({
  variant = 'outlined',
  value,
  label = 'Phone number',
  onChange,
  ...props
}) => {
  const getValueAfterDeletingClosedParanAtEnd = (event: React.ChangeEvent<HTMLInputElement>) => {
    const previousValue = String(value || '');
    const inputValue = event.currentTarget.value;

    const isDeletion = inputValue.length < previousValue.length;

    const hasDeletedCloseParanAtEnd =
      previousValue.endsWith(')') && !inputValue.endsWith(')') && isDeletion;

    if (hasDeletedCloseParanAtEnd) {
      // If the user deleted a closing parenthesis at the end, delete the last number along with it
      return AsYouTypeUS().input(inputValue.slice(0, -1));
    }

    return null;
  };

  const getValueAfterChangeAtEnd = (event: React.ChangeEvent<HTMLInputElement>) => {
    const previousValue = String(value || '');
    const inputValue = event.currentTarget.value;

    const cursorPosition = event.currentTarget.selectionStart || 0;

    // Check if the change is at the end of the string
    const isChangeAtEnd = cursorPosition === inputValue.length;

    const hasChanged = inputValue.length !== previousValue.length;

    if (isChangeAtEnd && hasChanged) {
      // Only format when adding characters at the end
      return AsYouTypeUS().input(inputValue);
    }

    return null;
  };

  const getValueEditingInMiddle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const previousValue = String(value || '');
    const inputValue = event.currentTarget.value;

    const cursorPosition = event.currentTarget.selectionStart || 0;
    const isChangeInTheMiddle = cursorPosition !== inputValue.length;

    if (isChangeInTheMiddle) {
      return AsYouTypeUS().input(previousValue);
    }

    return null;
  };

  const onPhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.currentTarget.value.replace(/[^\d +\-()]/g, '');

    const formattedValue =
      getValueAfterDeletingClosedParanAtEnd(event) ||
      getValueAfterChangeAtEnd(event) ||
      getValueEditingInMiddle(event) ||
      inputValue;

    onChange?.(formattedValue);
  };

  const labelId = `${props.id}-label`;

  return (
    <Stack className="phone-number" spacing={1}>
      <StyledLabel id={labelId}>{label}</StyledLabel>
      <TextField
        variant={variant}
        type="tel"
        value={value}
        onChange={onPhoneNumberChange}
        slotProps={{
          ...props.slotProps,
          htmlInput: {
            'aria-labelledby': labelId,
          },
        }}
        {...props}
      />
    </Stack>
  );
};
